import { useCallback } from "react";
import { cloneDeep } from "lodash";
import { logger } from "@/app/lib/logger";
import type { ChatMessage } from "@/types";
import type {
   SaveResponseToHistory,
   SetCurrentAttachments,
   SetHistory,
   SetStreamingResponse
} from "./types";

interface UseResponsePersistenceOptions {
   isSavingRef: React.MutableRefObject<boolean>;
   isCreatingNewChatRef: React.MutableRefObject<boolean>;
   currentAttachments: ChatMessage["attachments"];
   setHistory: SetHistory;
   setCurrentAttachments: SetCurrentAttachments;
   setStreamingResponse: SetStreamingResponse;
   isTemporaryChat: boolean;
   ownerShareId: string;
   userId?: string;
   historyItemId?: string;
   handleAddToHistory: (
      newHistory: ChatMessage[],
      historyChatId?: string
   ) => Promise<void>;
   handleCreateNewChat: (newHistory: ChatMessage[]) => Promise<void>;
}

export function useResponsePersistence({
   isSavingRef,
   isCreatingNewChatRef,
   currentAttachments,
   setHistory,
   setCurrentAttachments,
   setStreamingResponse,
   isTemporaryChat,
   ownerShareId,
   userId,
   historyItemId,
   handleAddToHistory,
   handleCreateNewChat
}: UseResponsePersistenceOptions): SaveResponseToHistory {
   return useCallback(
      async (response, options = {}) => {
         const {
            expandedQuery,
            metaData = [],
            placeholderRegistry,
            persistToServer = false,
            scope
         } = options;

         if (isSavingRef.current || !response.trim()) return;

         // The chat this response belongs to is no longer the visible view.
         // Skip both the UI update and client-side persistence so we never
         // corrupt the chat the user has switched to.
         if (scope && !scope.isActive()) return;

         isSavingRef.current = true;
         let newHistory: ChatMessage[] = [];
         setHistory((prev) => {
            const currentHistory = Array.isArray(prev) ? prev : [];
            const exists = currentHistory.some(
               (msg) => msg.role === "assistant" && msg.content === response
            );
            const filteredMetaData = [...metaData];

            if (!exists) {
               newHistory = [
                  ...currentHistory,
                  {
                     role: "assistant",
                     content: response,
                     metaData: filteredMetaData,
                     attachments: cloneDeep(currentAttachments),
                     expandedQuery: expandedQuery || undefined,
                     placeholderRegistry: placeholderRegistry || undefined
                  }
               ];
               return newHistory;
            }

            newHistory = currentHistory.map((msg, i) => {
               if (i === currentHistory.length - 1 && msg.role === "assistant") {
                  return {
                     ...msg,
                     metaData: filteredMetaData,
                     ...(expandedQuery && { expandedQuery: { ...expandedQuery } }),
                     ...(placeholderRegistry && { placeholderRegistry })
                  };
               }
               return msg;
            });

            return newHistory;
         });
         setCurrentAttachments([]);
         setStreamingResponse("");

         if (persistToServer) {
            setTimeout(async () => {
               if (isTemporaryChat) {
                  isSavingRef.current = false;
                  return;
               }
               const isOwnerShare = ownerShareId === userId;
               const isAddToHistoryAvailable = newHistory?.[0]?.id && historyItemId;
               try {
                  if (isAddToHistoryAvailable && isOwnerShare) {
                     await handleAddToHistory(newHistory, historyItemId);
                  } else {
                     isCreatingNewChatRef.current = true;
                     await handleCreateNewChat(newHistory);
                  }
               } catch (err) {
                  logger.error("[ChatStream] Save response server error:", err);
               } finally {
                  isSavingRef.current = false;
                  isCreatingNewChatRef.current = false;
               }
            }, 10);
         } else {
            isSavingRef.current = false;
         }
      },
      [
         currentAttachments,
         setHistory,
         setCurrentAttachments,
         setStreamingResponse,
         isTemporaryChat,
         ownerShareId,
         userId,
         historyItemId,
         handleAddToHistory,
         handleCreateNewChat,
         isSavingRef,
         isCreatingNewChatRef
      ]
   );
}
